const PROJECT_MODE = true;
const PROJECT_MODE_2 = true;
// toggle the scroll-position scaling effect in the suggestions scrollview
const SUGGESTION_SCALING_ENABLED = false;

import PropTypes from "prop-types";
import React from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  UIManager,
  Platform,
  Animated,
  ActivityIndicator,
} from "react-native";
import ENV from "app/utils/ENV";
import TouchableOpacity from "app/components/TouchableOpacity";
import Text from "app/components/Text";
import toastActions from "app/utils/toastActions";
import alertActions from "app/utils/alertActions";
import chapterActions from "app/utils/chapterActions";
import momentActions from "app/utils/momentActions";
import valueActions from "app/utils/valueActions";
import noteActions from "app/utils/noteActions";
import textHelpers from "app/utils/textHelpers";
import timeHelpers from "app/utils/timeHelpers";
import colorHelpers from "app/utils/colorHelpers";
import valueHelpers from "app/utils/valueHelpers";
import frequencyHelpers from "app/utils/frequencyHelpers";
import chapterHelpers from "app/utils/chapterHelpers";
import * as RootNavigation from "app/utils/RootNavigation.js";
import { styleSheet } from "app/utils/styleSheet";
import {
  MaterialCommunityIcons,
  AntDesign,
  Entypo,
  Ionicons,
  Feather,
} from "@expo/vector-icons";

import Icon from "app/components/Icon";
import TextInput from "app/components/TextInput";
import Autocomplete from "app/components/Autocomplete";
import Hoverable from "app/components/Hoverable";
import Tooltip from "app/components/Tooltip";
import HoverableTooltip from "app/components/HoverableTooltip";

import Badge from "app/components/chapter/Badge";
import BadgeRow from "app/components/chapter/BadgeRow";
import TimelineRow from "app/components/chapter/TimelineRow";
import ValueListItem from "app/components/ValueListItem";
import MomentBullet from "app/components/chapter/MomentBullet";
import MomentComposer from "app/components/chapter/MomentComposer";
import ScratchPad from "app/components/ScratchPad";
import JourneyMap from "app/components/JourneyMap";

import MomentsLog from "app/components/projects/MomentsLog";

import store from "app/redux/store";
import calendarActions from "app/utils/calendarActions";
import Target from "app/components/dragging/Target";

import { StyleContext } from "app/contexts/Style";

// Rollover ordering: most recent day first; within the same day, the item
// progressed EARLIEST that day comes first — the list reads "yesterday in
// the order it happened, then the day before" (last_seen_at from the server)
const sortRolloverSuggestions = (suggestions) =>
  [...(suggestions || [])].sort((a, b) => {
    const dayDiff = (a.value?.days_old ?? 0) - (b.value?.days_old ?? 0);
    if (dayDiff !== 0) return dayDiff;
    const tsA = a.value?.last_seen_at
      ? new Date(a.value.last_seen_at).getTime()
      : Infinity;
    const tsB = b.value?.last_seen_at
      ? new Date(b.value.last_seen_at).getTime()
      : Infinity;
    return tsA - tsB;
  });

export default class MomentList extends React.Component {
  static contextType = StyleContext;
  static propTypes = {
    backgroundColor: PropTypes.string,
  };

  constructor(props) {
    super(props);
    let showScratchPad = false;
    showScratchPad = true;
    // if (props.moment?.value?.is_note) showScratchPad = true;
    // if (props.hideBadgeRow) showScratchPad = false;
    let showMomentsLog = this.shouldShowMomentsLog(props);
    let chapterIsToday =
      !!props.chapter?.start_at &&
      timeHelpers.wasToday(timeHelpers.dateToDateTime(props.chapter.start_at)) &&
      timeHelpers.wasToday(timeHelpers.dateToDateTime(props.chapter.end_at));
    this.state = {
      loading: false,
      chapter: props.chapter,
      // expandedIds: props.expanded ? ["root"] : [],
      // expandedIds: [],
      moments: [],
      tasks: [],
      progressions: [],
      // newMoments: {},
      text: "",
      showAll: false,
      scopeToTimeRange: true,
      scopeToTimeRange: false,
      moreToShowOutsideTimeRange: false,
      showScratchPad,
      showMomentsLog,
      separateTasks: props.taskRow,
      // suggestions start expanded only for today's chapter on web —
      // collapsed on mobile and for every other chapter, where the badge
      // rows take vertical space the list itself needs
      expandSuggestions: ENV.WEB && chapterIsToday,
      expandRolloverSuggestions: ENV.WEB && chapterIsToday,
      // an expanded suggestion bullet lifts the scroller's height cap so
      // its scratchpad and moments row are actually viewable
      expandedSuggestionId: null,
      calendarSuggestions: [],
      composerText: "",
      composerKey: 0,
      focusedId: null,
      focusedRolloverId: null,
      rolloverScrollY: 0,
      locallyRemovedIds: new Set(),

      maxMomentsToShow: !props.chapter || props.chapter?.range == 1 ? 999 : 3,
      showCompleted:
        !!props.chapter ||
        props.parentIsChild ||
        props.parentIsValue ||
        props.showCompleted
          ? true
          : false,
      // showCompleted: true,
      maxMomentsToShow: 999,
      pinnedValueIds: [],
      unpinnedCompletedMoments: [],
      // hideScratchPad: PROJECT_MODE,
      hideScratchPad: false,
    };
    // Cache for memoizing setMoments results
    this._setMomentsCache = null;
    // Flag to prevent expensive setMoments during optimistic updates
    this._isOptimisticUpdate = false;
    // Debounced chapter-sort sync: rapid successive moves update the visual
    // order instantly and collapse into a single sort_moments request
    this._sortSyncTimeout = null;
    this._sortSyncInFlight = false;
    this._sortSyncQueued = false;
  }

  componentWillUnmount() {
    this._unmounted = true;
    // flush a pending sort sync so a quick navigation doesn't drop the reorder
    if (this._sortSyncTimeout) {
      clearTimeout(this._sortSyncTimeout);
      this._sortSyncTimeout = null;
      this.runSortSync();
    }
  }

  // While true, the local visual order is authoritative — prop-driven
  // setMoments must not clobber it (that's what made bullets jump back)
  sortSyncActive = () =>
    !!this._sortSyncTimeout || this._sortSyncInFlight || this._sortSyncQueued;

  componentDidMount = () => {
    this.setMoments();
    this.loadCalendarSuggestions();
  };

  loadCalendarSuggestions = async () => {
    let { chapter } = this.props;
    if (!chapter?.start_at || parseInt(chapter.range) !== 1) return;
    let events = await calendarActions.getEventsForChapter(
      chapter.start_at,
      chapter.end_at || chapter.start_at,
    );
    this.setState({ calendarSuggestions: events });
  };

  handleCalendarCreate = async (event) => {
    await calendarActions.dismissEvent(event.id);
    this.setState((prev) => ({
      composerText: event.summary,
      composerKey: prev.composerKey + 1,
      calendarSuggestions: prev.calendarSuggestions.filter(
        (e) => e.id !== event.id,
      ),
    }));
  };

  handleCalendarDismiss = async (event) => {
    await calendarActions.dismissEvent(event.id);
    this.setState((prev) => ({
      calendarSuggestions: prev.calendarSuggestions.filter(
        (e) => e.id !== event.id,
      ),
    }));
  };

  async componentDidUpdate(prevProps, prevState, snapshot) {
    // if (this.props.expanded != prevProps.expanded) {
    //   console.log("expanded changed", this.props.expanded);
    //   if (!this.state.expandedIds.includes("root")) {
    //     this.toggleExpanded("root");
    //   }
    // }
    // if (this.props.moments.length != prevProps.moments.length) {

    // Optimized: Check array length and IDs instead of expensive JSON.stringify
    const valueMomentsChanged =
      this.props.valueMoments?.length !== prevProps.valueMoments?.length ||
      this.props.valueMoments?.some((m, i) => m?.id !== prevProps.valueMoments?.[i]?.id);

    if (valueMomentsChanged) {
      // only ever auto-reveals — a manual "Show Moments" is never undone
      let showMomentsLog = this.shouldShowMomentsLog();
      if (!!showMomentsLog) {
        this.setState({ showMomentsLog });
      }
    }

    // Optimized: Check array references, length, and IDs instead of expensive JSON.stringify
    const momentsChanged =
      this.props.moments !== prevProps.moments ||
      this.props.moments?.length !== prevProps.moments?.length ||
      this.props.moments?.some((m, i) => m?.id !== prevProps.moments?.[i]?.id);

    const progressChanged =
      this.props.value?.progress_at !== prevProps.value?.progress_at ||
      this.props.value?.progress_at?.length !== prevProps.value?.progress_at?.length;

    if (
      momentsChanged ||
      progressChanged ||
      this.props.taskRow != prevProps.taskRow
    ) {
      // Skip expensive setMoments recalculation during optimistic updates
      if (this._isOptimisticUpdate) {
        this._isOptimisticUpdate = false;
        return;
      }
      // While a chapter sort sync is pending or in flight, the local order is
      // authoritative — ignore prop echoes so rows don't snap back mid-burst
      if (this.sortSyncActive()) return;

      if (this.props.taskRow && !prevProps.taskRow) {
        await this.setState({ separateTasks: true });
      } else if (!this.props.taskRow && prevProps.taskRow) {
        await this.setState({ separateTasks: false });
      }
      this.setMoments();
    }
  }

  // The moments row defaults hidden for parent values whose moments carry
  // no text — the children are the story there; "Show Moments" reveals it.
  // Values without children (or with written moments) keep the row visible.
  shouldShowMomentsLog = (props = this.props) => {
    let valueMoments = props.valueMoments || [];
    if (!valueMoments.length) return false;
    let hasMomentText = valueMoments.some((m) => !!m?.text?.length);
    if (hasMomentText) return true;
    let hasChildren = !!valueHelpers.getChildren({
      value: props.value || {},
      includeNotes: true,
    })?.length;
    return !hasChildren;
  };

  // whether a value's journey JSON holds anything worth showing
  journeyHasContent = (value) => {
    if (!value?.journey) return false;
    try {
      let parsed = JSON.parse(value.journey) || {};
      if (!!parsed.title?.length) return true;
      let beats = parsed.beats || {};
      return Object.keys(beats).some(
        (k) => !!beats[k]?.text?.length || !!beats[k]?.value_ids?.length,
      );
    } catch (e) {
      return false;
    }
  };

  setMoments = () => {
    let { value, moments, chapterMoments, chapter, rootChapter, taskRow } =
      this.props;
    let { scopeToTimeRange, separateTasks } = this.state;
    let moreToShowOutsideTimeRange = false;

    // Optimized: Check if we can use cached result to avoid expensive recalculation
    const cacheKey = `${moments?.length}-${value?.id}-${chapter?.id}-${rootChapter?.id}-${scopeToTimeRange}-${separateTasks}-${taskRow}`;
    if (this._setMomentsCache?.key === cacheKey && this._setMomentsCache?.moments === moments) {
      return;
    }

    // console.log("setting moments", moments);

    let { preferences, filters } = store.getState();
    // console.log(filters);
    // if (filters?.showHidden == 1)
    if (false) {
      moments = moments?.filter(
        (m) => !preferences?.hiddenIds?.includes(m.value?.id),
      );
    }
    // else if (filters?.showHidden == 2)
    //   moments = moments?.filter((m) =>
    //     preferences?.hiddenIds?.includes(m.value?.id)
    //   );

    let pinnedValueIds = moments?.map((m) => m.value_id) || [];
    let unpinnedCompletedMoments = moments?.filter(
      (m) => !!m?.value?.completed_at && !pinnedValueIds.includes(m.value?.id),
    );

    let progressions = [];
    if (!!value?.id) {
      progressions = [
        ...(value?.progress_at?.map((p, i) => ({
          category: "progress",
          event_id: `${value.id}-progress-${i}`,
          timestamp: p,
          value,
        })) || []),
        ...valueHelpers
          .getChildren({ value })
          .filter((c) => !!c.completed_at)
          .map((c) => ({
            category: "completion",
            event_id: `${c.id}-completion`,
            timestamp: c.completed_at,
            value: c,
          })),
      ];
      // emtpy slots
      let cycle = value.cycle || 7;
      // if (true || !!progressions.length) {
      if (value.cycle !== 0) {
        if (!value.completed_at) {
          if (progressions.length < cycle) {
            progressions = [
              ...progressions,
              ...new Array(cycle - progressions.length).fill(0).map((x, i) => ({
                category: "placeholder",
                event_id: `${value.id}-placeholder-${i}`,
                timestamp: null,
                value: {
                  // color: this.context.colors.fill,
                  color: this.context.colors.trim,
                  category: "placeholder",
                  icon_color: this.context.colors.empty,
                  value_id: value.id,
                },
              })),
            ];
          }
        }
      }
    } else {
      // console.log("no value to check progressions of");
    }

    // separate tasks
    let tasks = [];
    if (taskRow && separateTasks && !!moments?.length) {
      // console.log(moments);
      tasks = moments.filter((m) => valueHelpers.isTaskOrTracker(m.value));
      if (!!tasks.length) {
        tasks = [
          ...textHelpers.oldestFirst(
            tasks.filter((t) => !!t.completed_at),
            "completed_at",
          ),
          ...textHelpers.oldestFirst(
            tasks.filter((t) => !t.completed_at),
            "created_at",
          ),
        ];
        moments = moments.filter((m) => !valueHelpers.isTaskOrTracker(m.value));
        // console.warn("filtering out tasks", moments);
      }
    }

    // remove nested children
    if (!!chapterMoments?.length) {
      if (!!chapter?.id && chapter?.id == rootChapter?.id) {
        // remove children that will be nested

        moments = moments.filter((moment) => {
          let isPinnedChild = !!chapterHelpers.isNested(chapterMoments, moment);
          return !isPinnedChild;
        });
      } else {
        // remove children that are nested
        // moments = moments.filter((moment) => {
        //   if (chapterMoments.find((cm) => cm.id == moment.id)) {
        //     return false;
        //   }
        //   return true;
        // });
      }
    }

    // remove double moments
    if (!!chapterMoments?.length) {
      if (!!chapter?.id && chapter?.id == rootChapter?.id) {
        // remove additional instances of value's moment
        moments = moments.filter((moment) => {
          let alreadyPinned = false;
          for (let vid of moment?.value_ids || []) {
            if (
              chapterMoments.find(
                (cm) =>
                  cm.id != moment.id &&
                  cm.value_id == moment.value_id &&
                  // cm.value_ids.includes(vid) &&
                  cm.created_at < moment.created_at,
              )
            ) {
              alreadyPinned = true;
              break;
            }
          }
          return !alreadyPinned;
        });
      }
    }

    if (scopeToTimeRange && !chapterHelpers.atemporal(rootChapter)) {
      moments = moments.filter((moment) => {
        let value = moment.value;
        if (!!rootChapter?.start_at && !!rootChapter?.end_at) {
          let range = {
            startAt: timeHelpers.dateToDateTime(rootChapter.start_at),
            endAt: timeHelpers.endOfDay(
              timeHelpers.dateToDateTime(rootChapter.end_at),
            ),
          };
          if (
            !timeHelpers.withinRange(value.created_at, range) ||
            (!!value.inherited_timestamp &&
              !timeHelpers.withinRange(value.inherited_timestamp, range))
          ) {
            moreToShowOutsideTimeRange = true;
            return false;
          }
        }
        return true;
      });
    }
    this.setState({
      moments,
      tasks,
      progressions,
      moreToShowOutsideTimeRange,
      pinnedValueIds,
      unpinnedCompletedMoments,
    });

    // Cache the result
    this._setMomentsCache = { key: cacheKey, moments: this.props.moments };
  };

  showScratchPad = () => {
    this.setState({ showScratchPad: true });
  };

  render() {
    return <View>{this.renderNodes()}</View>;
  }

  renderNodes = () => {
    let {
      chapter,
      moment,
      atemporal,
      includeContext,
      timestamp,
      loading,
      nestPinnedChildren,
      nestedChildrenView,
      isToday,
      progress_at,
      rootChapter,
      chapterMoments,
      getUnscopedChildren,
      value,
      taskRow,
      parentExpanded,
      parentFocused,
      hideBadgeRow,
    } = this.props;
    let {
      moments,
      tasks,
      progressions,
      scopeToTimeRange,
      moreToShowOutsideTimeRange,
      draggingMomentId,
      showScratchPad,
      showMomentsLog,
      separateTasks,
      focusedId,
      maxMomentsToShow,
      showCompleted,
      pinnedValueIds,
      unpinnedCompletedMoments,
      hideScratchPad,
    } = this.state;

    if (this.context.emptyMode) moments = [];

    if (!!getUnscopedChildren || parentExpanded) {
      maxMomentsToShow = 999;
    }

    let sortedMoments = textHelpers.oldestFirst(moments, "created_at");
    let values = sortedMoments
      ?.map((m) => m.value || m.note)
      .filter((v) => !!v);
    // let notes = values.filter((v) => v?.is_note);
    // let mostRecentNote = notes[0];

    // let moreToShow = false;

    if (!moments?.length && !tasks?.length && loading) {
      return null;
    }

    // let progressions = [];
    let nonProgrsessionMoments = [];

    let displayShowOlderButton =
      !!getUnscopedChildren && this.props.parentExpanded;

    // items.forEach((m) => {
    //   let value = m.value || m.note;
    //   if (!value) {
    //     // value = values.find((n) => n.id == m.value_id) || m.note;
    //   }
    //   // console.log(m, value);
    //   if (!value) {
    //     console.error("missing value...", m, values);
    //     return;
    //   }

    //   if (!showAll && !!timestamp && !nestedChildrenView) {
    //     if (
    //       timeHelpers.dateTimeToDate(value.created_at) <
    //       timeHelpers.dateTimeToDate(timestamp)
    //     ) {
    //       // moreToShow = true;
    //       // return;
    //     }
    //   }

    //   let valueIsOngoing = !value.completed_at;
    //   valueIsOngoing = false;
    //   let valueWasCreatedToday;
    //   if (valueIsOngoing) {
    //     valueWasCreatedToday = timeHelpers.wasToday(
    //       value.created_at,
    //       m.created_at
    //     );
    //   }

    //   // if (valueIsOngoing && !valueWasCreatedToday && !m.note) {
    //   if (valueIsOngoing && !m.note && !!m.completed_at && !expanded) {
    //     progressions.push(m);
    //     // nonProgrsessionMoments.push({ ...m, value, progression: true });
    //   } else {
    //     if (value.is_note && !showAllNotes && value.id != mostRecentNote?.id) {
    //       // only include most recent note
    //       return;
    //     }
    //     let stickyChildrenIds = [];
    //     let stickyChildMoments = [];
    //     if (nestPinnedChildren || true) {
    //       if (items.find((i) => value?.parent_ids.includes(i.value.id))) {
    //         // dont allow any parents to be a sticky child?
    //         // when would this be the case?
    //         return;
    //       }
    //       stickyChildMoments = items.filter((i) =>
    //         i.value?.parent_ids?.includes(value.id)
    //       );
    //       stickyChildrenIds = stickyChildMoments.map((c) => c.value.id);
    //     }
    //     nonProgrsessionMoments.push({
    //       id: value.id,
    //       ...m,
    //       inherited_timestamp: value.inherited_timestamp,
    //       value,
    //       // stickyChildrenIds,
    //       stickyChildMoments,
    //     });
    //   }
    // });

    // let moreToShowOutsideTimeRange = false;

    let toggleScratchPadButton = (
      <TouchableOpacity
        onPress={() => this.setState({ showScratchPad: !showScratchPad })}
      >
        <MaterialCommunityIcons
          name={"text"}
          size={this.context.sizes.large * this.context.sizes.mobileIncrease}
          style={{
            color:
              !!value?.text?.length && value?.text != value?.title
                ? this.context.colors.foreground
                : this.context.colors.trim,
            marginRight: this.context.sizes.tiny,
          }}
        />
      </TouchableOpacity>
    );

    return (
      <View
        ref={(ref) => (this.ref = ref)}
        style={[
          !!chapter?.id && chapter.id == rootChapter?.id && { minHeight: 60 },
        ]}
        // onLayout={({ nativeEvent }) => {
        //   if (this.ref) {
        //     this.ref.measure((x, y, width, height, pageX, pageY) => {
        //       let target = {
        //         // chapterId: chapter.id,
        //         id: chapter.id,
        //         x: pageX,
        //         y: pageY,
        //         width,
        //         height,
        //         moment_ids: items.map((i) => i.id),
        //       };
        //       store.dispatch({ type: "dragging/add_chapter_target", target });
        //     });
        //   }
        // }}
      >
        {this.renderSuggestions()}

        {false &&
          //!!value?.text?.length &&
          !hideBadgeRow &&
          parentExpanded &&
          !!progressions?.length &&
          !value?.is_note &&
          !(
            value.cycle === 0 &&
            !progressions.find((p) => p.category != "placeholder")
          ) &&
          //(!this.context.pensieve ||
          (true || !value.child_ids?.length || !!value.progress_at.length) &&
          ENV.WEB && (
            <View
              style={[
                {
                  marginBottom: this.context.sizes.tiny,
                  zIndex: ENV.WEB ? 99 : 0, // badge tooltips ontop for web, child's parent tags ontop for mobile
                  justifyContent: "center",
                  alignItems: "flex-start",
                  alignItems: "stretch",
                },
              ]}
            >
              {false ? (
                <BadgeRow
                  index={0}
                  values={values}
                  moments={progressions}
                  maxLength={value.cycle || 7}
                  moment={moment}
                  valueColor={value.color}
                  chapter={chapter}
                  smallBadges={ENV.WEB}
                  button={
                    !!moment?.id &&
                    typeof moment.id == "number" && (
                      <HoverableTooltip
                        text={
                          !!moment.completed_at ? "Re-open" : "Manual cross off"
                        }
                      >
                        <View
                          style={{
                            marginLeft: this.context.sizes.base,
                            justifyContent: "center",
                          }}
                        >
                          <Entypo
                            name={"check"}
                            color={
                              !moment.completed_at
                                ? this.context.colors.trim
                                : valueHelpers.getColor(value)
                            }
                            // size={iconSize}
                            style={[
                              styleSheet.bullet,
                              {
                                // borderColor: valueHelpers.getColor(value),
                                borderWidth: !moment.completed_at ? 1 : 2,
                                borderColor: !moment.completed_at
                                  ? this.context.colors.trim
                                  : valueHelpers.getColor(value),
                                // backgroundColor: !!moment.completed_at
                                //   ? valueHelpers.getColor(value)
                                //   : this.context.colors.empty,
                              },
                            ]}
                            onPress={async () => {
                              if (
                                !(await alertActions.confirm({
                                  title: !!moment.completed_at
                                    ? "Re-open item?"
                                    : "Cross off item?",
                                  subtitle: !moment.completed_at
                                    ? "This will cross the item off this list without completing or progressing it."
                                    : "This will re-open the item without removing the logged progress.",
                                }))
                              )
                                return;
                              let res;
                              if (!!moment.completed_at)
                                res = await momentActions.uncomplete(
                                  this.props.moment.id,
                                );
                              else
                                res = await momentActions.complete(
                                  this.props.moment.id,
                                );
                              !!this.props.onUpdateMoment &&
                                this.props.onUpdateMoment({
                                  ...this.props.moment,
                                  completed_at: res.moment.completed_at,
                                });
                            }}
                          />
                          <View
                            pointerEvents="none"
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: 0,
                              right: 0,
                              height: 1,
                              backgroundColor: !moment.completed_at
                                ? this.context.colors.trim
                                : valueHelpers.getColor(value),
                            }}
                          />
                        </View>
                      </HoverableTooltip>
                    )
                  }
                  allowProgressOnPressPlaceholder
                  firstButton={() => toggleScratchPadButton}
                />
              ) : (
                <TimelineRow
                  value={value}
                  values={values}
                  moments={progressions}
                  moment={moment}
                  color={value.color}
                  chapter={chapter}
                />
              )}
            </View>
          )}

        {hideBadgeRow && !!value?.text?.length && (
          <View
            style={{
              marginLeft: this.context.sizes.base,
            }}
          >
            {toggleScratchPadButton}
          </View>
        )}

        {/*items?.map((item, i) => {
          // remove moments from previous chapters
          let mutatedItem = nonProgrsessionMoments?.find(
            (m) => m.id == item.id
          );
          if (!mutatedItem) return null;

          if (scopeToTimeRange) {
            if (!!rootChapter?.start_at && !!rootChapter?.end_at) {
              let range = {
                startAt: timeHelpers.dateToDateTime(rootChapter.start_at),
                endAt: timeHelpers.endOfDay(
                  timeHelpers.dateToDateTime(rootChapter.end_at)
                ),
              };
              if (
                !timeHelpers.withinRange(item.created_at, range) ||
                (!!mutatedItem?.inherited_timestamp &&
                  !timeHelpers.withinRange(
                    mutatedItem.inherited_timestamp,
                    range
                  ))
              ) {
                moreToShowOutsideTimeRange = true;
                return null;
              }
            }
          */}

        {showScratchPad &&
          parentExpanded &&
          (hideScratchPad && !["note", "event"].includes(value?.category) ? (
            <Text
              button
              style={{ alignSelf: "center" }}
              onPress={() => this.setState({ hideScratchPad: false })}
            >
              Scratchpad
            </Text>
          ) : (
            <ScrollView
              nestedScrollEnabled
              keyboardShouldPersistTaps={"always"}
              contentContainerStyle={{
                // flex: 1,
                // borderWidth: 1,
                borderColor: "green",
              }}
              style={{
                // maxHeight: 100,
                // borderWidth: 3,
                marginTop: this.context.sizes.base,
                borderColor: "red",
                flex: 1,
                minHeight: 40,
                maxHeight: ENV.WEB ? undefined : 300,
                maxWidth: hideBadgeRow
                  ? ENV.WEB
                    ? 400
                    : this.context.sizes.WIDTH
                  : undefined,
              }}
            >
              <ScratchPad
                // readOnly={PROJECT_MODE}
                inline
                placeholder={"What is this?"}
                onEscape={this.props.onBrowseEscape}
                value={value}
                ref={(r) => (this.scratPadRef = r)}
                focusPrevious={this.props.focusParentMoment}
                moment={moment}
                expandable
                focusNext={() => {
                  if (this.momentsLogRef?.focus) this.momentsLogRef.focus();
                  else
                    this.handleFocusFirstMoment &&
                      this.handleFocusFirstMoment("scratchpad");
                }}
              />
            </ScrollView>
          ))}

        {/* mini hero's-journey map when the value has journey data —
            only while the bullet is expanded, same gate as the scratchpad */}
        {showScratchPad &&
          parentExpanded &&
          this.journeyHasContent(value) && (
            <View style={{ marginTop: this.context.sizes.small }}>
              <JourneyMap mini value={value} />
            </View>
          )}

        {(showMomentsLog || parentExpanded) &&
          showScratchPad &&
          parentExpanded &&
          PROJECT_MODE &&
          !!this.props.valueMoments &&
          (showMomentsLog ? (
            <MomentsLog
              value={value}
              moments={this.props.valueMoments}
              onBrowseEscape={this.props.onBrowseEscape}
              ref={(r) => (this.momentsLogRef = r)}
              chapter={rootChapter}
              chapters={this.props.valueMomentsChapters}
              moment={moment}
              onRefreshChapter={this.props.onRefreshChapter}
              onRefreshMoment={this.props.onRefreshMoment}
              focusPrevious={() => {
                // scratchpad now sits above the log
                this.scratPadRef?.focus && this.scratPadRef.focus();
              }}
              focusNext={() => {
                this.handleFocusFirstMoment &&
                  this.handleFocusFirstMoment("scratchpad");
              }}
            />
          ) : (
            <Text
              // button
              style={{ alignSelf: "flex-end", color: this.context.colors.trim }}
              onPress={() => this.setState({ showMomentsLog: true })}
            >
              Show Moments
            </Text>
          ))}

        {/*<Text
              style={{ marginLeft: this.context.sizes.base }}
              numberOfLines={!this.state.expanedNote && 3}
              onPress={() => this.setState({ expanedNote: true })}
            >
              {value.text}
            </Text>*/}

        {(!!tasks.length || separateTasks) && (
          // {(!!tasks.length || separateTasks == false) && (
          <View style={[{ zIndex: 1 }]}>
            <BadgeRow
              index={0}
              moments={tasks}
              title={"Tasks:"}
              // onPressTitle={async () => {
              //   await this.setState({ separateTasks: !separateTasks });
              //   this.setMoments();
              // }}
              chapter={chapter}
              rootChapter={chapter}
              chapterMoments={tasks}
            />
          </View>
        )}

        {(!!this.props.suggestions?.length || !!this.props.rolloverSuggestions?.length) && !!moments?.length && (
          <View style={{ borderBottomWidth: 1, borderColor: this.context.colors.trim, opacity: 0.15, marginVertical: 4 }} />
        )}

        {moments?.map((moment, i) => {
          let item = moment;
          if (!item) return null;
          if (i >= maxMomentsToShow) return null;
          let isPinned = pinnedValueIds.includes(item.value?.id);
          if (!showCompleted && !!item.value?.completed_at && !isPinned)
            return null;

          return (
            <Hoverable key={item.id}>
              {(isHovered) => (
                <View
                  style={[
                    (isHovered || focusedId == item.id) && {
                      zIndex: focusedId == item.id ? 3 : 2,
                    },
                  ]}
                >
                  <MomentBullet
                    onSelectValue={this.props.onSelectValue}
                    onBrowseEscape={this.props.onBrowseEscape}
                    bonusButtonText={this.props.bonusButtonText}
                    bonusButtonAction={() =>
                      !!this.props.bonusButtonAction &&
                      this.props.bonusButtonAction(item.value.id)
                    }
                    handleDelete={this.props.handleDelete}
                    includeAttribution={this.props.includeAttribution}
                    key={`${i}-${item.id}`}
                    inputRef={(c) =>
                      (this[`ref-${i}-${item.id || item.value?.id}`] = c)
                    }
                    ref={(c) =>
                      (this[`bullet-ref-${i}-${item.id || item.value?.id}`] = c)
                    }
                    // noIcon={item.value?.is_note}
                    moment={item}
                    badgeScale={this.props.badgeScale}
                    textColor={this.props.textColor}
                    // value={item.value}
                    chapter={chapter}
                    parentIsValue={this.props.parentIsValue}
                    pinnedMomentList={this.props.pinnedMomentList} // uncomet to use small styling for pinned children
                    pinnedMomentParent={this.props.pinnedMomentParent}
                    firstPinnedChild={this.props.pinnedMomentList && i == 0}
                    hideScrathpadPreview={this.props.hideScrathpadPreview}
                    parentId={this.props.parentId}
                    parentIsChild={this.props.parentIsChild}
                    rootChapter={rootChapter}
                    onRefreshChapter={this.props.onRefreshChapter}
                    onUpdateChapter={this.props.onUpdateChapter}
                    onUpdateMoment={(moment) => {
                      this.props.onUpdateMoment &&
                        this.props.onUpdateMoment(moment);
                    }}
                    onCollapseChapter={this.props.onCollapse}
                    onExpandChapter={this.props.onExpand}
                    onCollapse={this.props.onCollapseMoment}
                    onExpand={this.props.onExpandMoment}
                    chapterExpanded={this.props.chapterExpanded}
                    focusParentComposer={() => {
                      this.handleFocusComposer();
                    }}
                    // parentExpanded={expanded}
                    onFocus={() => {
                      this.setState({ focusedId: item.id });
                      !!this.props.scrollToChapter &&
                        this.props.scrollToChapter();
                      !!this.props.onFocusMoment &&
                        this.props.onFocusMoment(item);
                    }}
                    sortItemUp={() => this.handleSortItemUp(item)}
                    sortItemDown={() => this.handleSortItemDown(item)}
                    sortItemToBottom={() => this.handleSortItemToBottom(item)}
                    sortItemToTop={() => this.handleSortItemToTop(item)}
                    focusPrevious={() =>
                      this.handleFocusPrevious({
                        moment: item,
                        id: item.id,
                        index: i,
                      })
                    }
                    focusNext={(comingFromChild = false) =>
                      this.handleFocusNext({
                        moment: item,
                        id: item.id,
                        index: i,
                        comingFromChild,
                      })
                    }
                    rightAlign={this.props.rightAlign}
                    focusParentMoment={() =>
                      this.handleFocusPrevious({
                        moment: "self",
                        id: item.id,
                        index: i,
                      })
                    }
                    onCollapseParent={() => {
                      // if (!!this.props.onCollapse) {
                      //   this.props.onCollapse();
                      // } else
                      if (!!this.props.collapseParent) {
                        this.props.collapseParent();
                      }
                    }}
                    onFocusPreviousChapter={this.props.onFocusPreviousChapter}
                    onFocusNextChapter={this.props.onFocusNextChapter}
                    onPinToParentChapter={this.props.onPinToParentChapter}
                    atemporal={atemporal}
                    includeContext={includeContext}
                    // stickyChildrenIds={item.stickyChildrenIds}
                    // stickyChildMoments={item.stickyChildMoments}
                    chapterMoments={chapterMoments?.filter(
                      // remove tasks
                      (m) => !tasks.find((t) => t.value?.id == m.value?.id),
                    )}
                    horizontal={this.props.horizontal}
                    expanded={this.props.expanded}
                    hideTags={this.props.hideTags}
                    hideBadgeRow={this.props.hideBadgeRow}
                    preventAutoFocus={this.props.preventAutoFocus}
                    valueBadge={this.props.valueBadges}
                    largeBullet={isToday}
                    scopeToChapter={this.props.scopeToChapter}
                    selectable={this.props.selectable}
                    onSelectValue={this.props.onSelectValue}
                    selectedIds={this.props.selectedIds}
                    onFocusChapterTitle={this.props.onFocusChapterTitle}
                    onStartDraggingBadge={(id) =>
                      this.setState({ draggingMomentId: id })
                    }
                    onEndDraggingBadge={() =>
                      this.setState({ draggingMomentId: null })
                    }
                    displayDraggableTarget={
                      !!draggingMomentId && draggingMomentId != item.id
                    }
                    displayDraggableTarget={!!draggingMomentId}
                    index={i}
                    shadeOnHover
                    onEscape={this.props.onEscape}
                  />
                </View>
              )}
            </Hoverable>
          );
        })}

        {!displayShowOlderButton &&
          !showCompleted &&
          !!unpinnedCompletedMoments?.length && (
            <TouchableOpacity
              onPress={() => this.setState({ showCompleted: true })}
            >
              <View style={{ flexDirection: "row", opacity: 0.5 }}>
                <Text>Show {unpinnedCompletedMoments?.length} complete</Text>
              </View>
            </TouchableOpacity>
          )}

        {moments?.length > maxMomentsToShow && (
          <TouchableOpacity
            onPress={() => this.setState({ maxMomentsToShow: 999 })}
          >
            <View style={{ flexDirection: "row", opacity: 0.5 }}>
              <Text>+{moments.length - maxMomentsToShow} more</Text>
              <BadgeRow
                // tags
                index={0}
                moments={moments.slice(maxMomentsToShow)}
                wrapperStyle={{ zIndex: 3 }}
                // title={"Inherited Suggestions:"}
                // titleTooltip={"Inherited Suggestions"}
                // title={
                //   <Ionicons
                //     name="arrow-redo-sharp"
                //     style={{
                //       fontSize: this.context.sizes.smallItemHeight,
                //       paddingRight: this.context.sizes.base,
                //       transform: [{ rotateX: "180deg" }],
                //     }}
                //     color={this.context.colors.trim}
                //   />
                // }
                chapter={chapter}
                rootChapter={chapter}
                onUpdateChapter={this.props.onUpdateChapter}
                onPressTitle={async () => {
                  await this.setState({
                    expandSuggestions: !expandSuggestions,
                  });
                }}
                includeAttribution={"left"}
              />
            </View>
          </TouchableOpacity>
        )}

        {/*(!!chapter?.id || this.props.expanded) &&
          scopeToTimeRange &&
          moreToShowOutsideTimeRange && (
            <Text onPress={() => this.setState({ scopeToTimeRange: false })}>
              Show All
            </Text>
          )*/}

        {displayShowOlderButton && (
          <TouchableOpacity onPress={getUnscopedChildren}>
            <View style={{ flexDirection: "row", opacity: 0.5 }}>
              <View
                style={[
                  styleSheet.bullet,
                  !ENV.WEB && styleSheet.largeBullet,
                  !ENV.WEB && styleSheet.mobileLargeBullet,
                  { borderWidth: 0 },
                ]}
              >
                <Text
                  style={{
                    fontSize: ENV.WEB
                      ? this.context.sizes.small
                      : this.context.sizes.default,
                    lineHeight: ENV.WEB
                      ? this.context.sizes.large
                      : this.context.sizes.largeBulletWidth,
                  }}
                >
                  •••
                </Text>
              </View>
              <Text
                style={
                  !ENV.WEB && {
                    paddingVertical: this.context.sizes.largeBulletWidth / 2,
                  }
                }
              >
                Show more{/*older*/}
              </Text>
            </View>
          </TouchableOpacity>
        )}

        {/*!showAll && moreToShow && (
          <Text onPress={() => this.setState({ showAll: true })}>
            Show old notes
          </Text>
        )*/}

        {!nestedChildrenView && (
          <Target
            action={this.props.parentIsValue ? "add_child" : "creat_moment"}
            chapterId={chapter?.id}
            valueId={this.props.parentIsValue && this.props.parentId}
          />
        )}

        {!nestedChildrenView && this.renderCalendarSuggestions()}
        {!nestedChildrenView && this.renderInput()}

        {!nestedChildrenView && this.renderReferencers()}
      </View>
    );
  };

  expandAll = () => {
    let { moments } = this.state;
    moments.forEach((moment, i) => {
      let ref = this[`bullet-ref-${i}-${moment.id}`];
      if (!!ref) ref.toggleExpanded(true, true);
    });
  };

  renderSuggestions = () => {
    let { suggestions, rolloverSuggestions, loading, chapter } = this.props;
    let { focusedRolloverId, locallyRemovedIds } = this.state;
    suggestions = (suggestions || []).filter((s) => !locallyRemovedIds.has(s.value?.id));
    rolloverSuggestions = (rolloverSuggestions || []).filter((s) => !locallyRemovedIds.has(s.value?.id));
    const sortedRollover = sortRolloverSuggestions(rolloverSuggestions);

    if (!this.props.showSuggestions) return null;

    let { expandSuggestions, expandRolloverSuggestions } = this.state;

    if (
      (!!suggestions?.length || !!rolloverSuggestions?.length) &&
      !loading
    ) {
      const iconStyle = {
        fontSize: this.context.sizes.smallItemHeight,
        paddingRight: this.context.sizes.base,
      };
      return (
        <View style={{ marginBottom: this.context.sizes.base, zIndex: 2 }}>

          {/* Inherited Suggestions */}
          {!!suggestions?.length && !expandSuggestions && (
            <BadgeRow
              index={0}
              moments={suggestions}
              wrapperStyle={{ zIndex: 3 }}
              titleTooltip={"Inherited Suggestions"}
              title={
                <Ionicons
                  name="arrow-redo-sharp"
                  style={{ ...iconStyle, transform: [{ rotateX: "180deg" }] }}
                  color={this.context.colors.trim}
                />
              }
              chapter={chapter}
              rootChapter={chapter}
              onUpdateChapter={this.props.onUpdateChapter}
              onPressTitle={() => this.setState({ expandSuggestions: true })}
              onDoublePressTitle={this.handleSuggestionActions}
              includeAttribution={"left"}
              doublePressActions={["Pin", "Dismiss", "Snooze"]}
            />
          )}
          {/* Rollover Suggestions */}
          {!!rolloverSuggestions?.length && !expandRolloverSuggestions && (
            <BadgeRow
              index={0}
              doublePressActions={["Pin", "Dismiss", "Snooze"]}
              moments={sortedRollover}
              wrapperStyle={{ zIndex: 2 }}
              titleTooltip={"Rollover Suggestions"}
              title={
                <MaterialCommunityIcons
                  name="application-import"
                  style={iconStyle}
                  color={this.context.colors.trim}
                />
              }
              chapter={chapter}
              rootChapter={chapter}
              onUpdateChapter={this.props.onUpdateChapter}
              onPressTitle={() => this.setState({ expandRolloverSuggestions: true })}
              includeAttribution={"left"}
            />
          )}
          {(expandSuggestions || expandRolloverSuggestions) && (() => {
            const sugItems = expandSuggestions ? (suggestions || []) : [];
            const rolItems = expandRolloverSuggestions ? sortedRollover : [];
            const totalItems = sugItems.length + rolItems.length;
            if (totalItems === 0) return null;
            const scrollY = this.state.rolloverScrollY;
            const estimatedNaturalHeight = this.context.sizes.default * 1.6 * 1.8;
            const viewportHeight = this.context.sizes.default * 1.6 * 2.5 * 1.8;
            // peak (100% scale) sits slightly below the topmost position rather than right at the top
            const peakOffset = viewportHeight * 0.15;
            // approx left-badge width, used to nudge shrinking items inward so badges stay column-aligned
            const badgeWidth = this.context.sizes.default * 1.6;
            return (
              <ScrollView
                style={{
                  maxHeight: this.state.expandedSuggestionId
                    ? undefined
                    : this.context.sizes.default * 1.6 * 2.16 * 1.8,
                  // subtle tint marks the expanded suggestion area apart
                  // from the chapter's real pins below
                  backgroundColor: colorHelpers.hexToRgbaString(
                    this.context.colors.trim,
                    0.18,
                  ),
                  borderRadius: this.context.sizes.smallBorderRadius,
                  paddingVertical: this.context.sizes.tiny,
                }}
                contentInset={{ bottom: this.context.sizes.default * 1.6 * 2 * 1.8 }}
                nestedScrollEnabled
                scrollEventThrottle={32}
                onScroll={(e) => this.setState({ rolloverScrollY: e.nativeEvent.contentOffset.y })}
              >
                {sugItems.map((item, i) => {
                  const estimatedY = i * estimatedNaturalHeight;
                  // 100% scale lands slightly below the top, and stays clamped at 100% above that point
                  const positionFactor = Math.max(0, Math.min(1, (estimatedY - scrollY - peakOffset) / viewportHeight));
                  const scale = !SUGGESTION_SCALING_ENABLED
                    ? 1.0
                    : ENV.WEB
                    ? Math.max(0.68, 1.0 - positionFactor * 0.32)
                    : Math.max(0.25, 1.0 - positionFactor * 0.75);
                  const marginVertical = Math.round(-(estimatedNaturalHeight * (1 - scale)) / 2);
                  // shift shrinking items in from the left so left badges stay on a central column
                  const marginLeft = Math.round((badgeWidth * (1 - scale)) / 2);
                  return (
                    <View
                      key={`sug-${i}-${item.id}`}
                      style={{ opacity: 0.8, transform: [{ scale }], transformOrigin: "left center", marginVertical, marginLeft }}
                    >
                      <MomentBullet
                        textOpacity={0.4}
                        inputRef={(c) => (this[`ref-s-${i}-${item.id || item.value?.id}`] = c)}
                        ref={(c) => (this[`bullet-ref-s-${i}-${item.id || item.value?.id}`] = c)}
                        moment={item}
                        chapter={chapter}
                        rootChapter={chapter}
                        includeAttribution={"left"}
                        index={i}
                        shadeOnHover
                        handleDelete={(valueId) => {
                          this.setState((prev) => ({ locallyRemovedIds: new Set([...prev.locallyRemovedIds, valueId]) }));
                          chapterActions.dismissSuggestion(chapter, valueId);
                          toastActions.show("Suggestions snoozed for today");
                        }}
                        onPinToParentChapter={this.props.onPinToParentChapter}
                        focusParentMoment={() => this[`ref-s-${i}-${item.id || item.value?.id}`]?.focus()}
                        onEscape={() => this.setState({ expandSuggestions: false })}
                        onFocusChapterTitle={this.props.onFocusChapterTitle}
                        chapterExpanded
                        focusParentComposer={this.focusFirstMomentOrComposer}
                        onExpand={() =>
                          this.setState({ expandedSuggestionId: item.id || item.value?.id })
                        }
                        onCollapse={() =>
                          this.setState((s) =>
                            s.expandedSuggestionId === (item.id || item.value?.id)
                              ? { expandedSuggestionId: null }
                              : null,
                          )
                        }
                        focusPrevious={() => {
                          if (i === 0) {
                            this.props.onFocusChapterTitle?.();
                          } else {
                            const prev = sugItems[i - 1];
                            this[`ref-s-${i - 1}-${prev.id || prev.value?.id}`]?.focus();
                          }
                        }}
                        focusNext={() => {
                          if (i === sugItems.length - 1) {
                            if (rolItems.length) {
                              const first = rolItems[0];
                              this[`ref-r-0-${first.id || first.value?.id}`]?.focus();
                            } else {
                              this.handleFocusFirstMoment();
                            }
                          } else {
                            const next = sugItems[i + 1];
                            this[`ref-s-${i + 1}-${next.id || next.value?.id}`]?.focus();
                          }
                        }}
                      />
                    </View>
                  );
                })}
                {rolItems.map((item, i) => {
                  const combinedI = sugItems.length + i;
                  const daysOld = item.value?.days_old ?? 0;
                  const isFocused = focusedRolloverId === (item.id || item.value?.id);
                  const ageLabel = daysOld === 0 ? "today" : `${daysOld}d`;
                  const estimatedY = combinedI * estimatedNaturalHeight;
                  // 100% scale lands slightly below the top, and stays clamped at 100% above that point
                  const positionFactor = isFocused ? 0 : Math.max(0, Math.min(1, (estimatedY - scrollY - peakOffset) / viewportHeight));
                  const scale = !SUGGESTION_SCALING_ENABLED
                    ? 1.0
                    : ENV.WEB
                    ? Math.max(0.68, 1.0 - positionFactor * 0.32)
                    : Math.max(0.25, 1.0 - positionFactor * 0.75);
                  const marginVertical = Math.round(-(estimatedNaturalHeight * (1 - scale)) / 2);
                  // shift shrinking items in from the left so left badges stay on a central column
                  const marginLeft = Math.round((badgeWidth * (1 - scale)) / 2);
                  return (
                    <View
                      key={`rol-${item.id || item.value?.id}`}
                      style={{ flexDirection: "row", alignItems: "center", transform: [{ scale }], transformOrigin: "left center", marginVertical, marginLeft }}
                    >
                      <View style={{ flex: 1 }}>
                        <MomentBullet
                          textOpacity={0.4}
                          inputRef={(c) => (this[`ref-r-${i}-${item.id || item.value?.id}`] = c)}
                          ref={(c) => (this[`bullet-ref-r-${i}-${item.id || item.value?.id}`] = c)}
                          moment={item}
                          chapter={chapter}
                          rootChapter={chapter}
                          includeAttribution={"left"}
                          index={i}
                          shadeOnHover
                          handleDelete={(valueId) => {
                            this.setState((prev) => ({ locallyRemovedIds: new Set([...prev.locallyRemovedIds, valueId]) }));
                            chapterActions.dismissSuggestion(chapter, valueId);
                            toastActions.show("Suggestions snoozed for today");
                          }}
                          handleSnoozeWeek={(valueId) => {
                            this.setState((prev) => ({ locallyRemovedIds: new Set([...prev.locallyRemovedIds, valueId]) }));
                            const snoozeDays = Math.max(1, 7 - daysOld);
                            const snoozeUntil = new Date();
                            snoozeUntil.setDate(snoozeUntil.getDate() + snoozeDays);
                            snoozeUntil.setHours(0, 0, 0, 0);
                            valueActions.update(valueId, { snooze_until: snoozeUntil.toISOString() });
                            const label = snoozeDays === 1 ? "1 day" : `${snoozeDays} days`;
                            toastActions.show(`Suggestion dismissed for ${label}`);
                          }}
                          onPinToParentChapter={this.props.onPinToParentChapter}
                          focusParentMoment={() => this[`ref-r-${i}-${item.id || item.value?.id}`]?.focus()}
                          onFocus={() => this.setState({ focusedRolloverId: item.id || item.value?.id })}
                          onEscape={() => this.setState({ expandSuggestions: false, expandRolloverSuggestions: false })}
                          onFocusChapterTitle={this.props.onFocusChapterTitle}
                          chapterExpanded
                          focusParentComposer={this.focusFirstMomentOrComposer}
                          onExpand={() =>
                            this.setState({ expandedSuggestionId: item.id || item.value?.id })
                          }
                          onCollapse={() =>
                            this.setState((s) =>
                              s.expandedSuggestionId === (item.id || item.value?.id)
                                ? { expandedSuggestionId: null }
                                : null,
                            )
                          }
                          focusPrevious={() => {
                            if (i === 0) {
                              this.setState({ focusedRolloverId: null });
                              if (sugItems.length) {
                                const last = sugItems[sugItems.length - 1];
                                this[`ref-s-${sugItems.length - 1}-${last.id || last.value?.id}`]?.focus();
                              } else {
                                this.props.onFocusChapterTitle?.();
                              }
                            } else {
                              const prev = rolItems[i - 1];
                              this[`ref-r-${i - 1}-${prev.id || prev.value?.id}`]?.focus();
                            }
                          }}
                          focusNext={() => {
                            if (i === rolItems.length - 1) {
                              this.setState({ focusedRolloverId: null });
                              this.handleFocusFirstMoment();
                            } else {
                              const next = rolItems[i + 1];
                              this[`ref-r-${i + 1}-${next.id || next.value?.id}`]?.focus();
                            }
                          }}
                        />
                      </View>
                      {scale >= 1.0 && (
                        <Text style={{ fontSize: 11, color: this.context.colors.trim, marginRight: 8, minWidth: 28, textAlign: "right" }}>
                          {ageLabel}
                        </Text>
                      )}
                    </View>
                  );
                })}
                {!ENV.WEB && (() => {
                  const estimatedY = totalItems * estimatedNaturalHeight;
                  const positionFactor = Math.max(0, Math.min(1, (estimatedY - scrollY) / (this.context.sizes.default * 1.6 * 5 * 1.8)));
                  const scale = Math.max(0.35, 1.0 - positionFactor * 0.65);
                  const marginVertical = Math.round(-(estimatedNaturalHeight * (1 - scale)) / 2);
                  return (
                    <View style={{ transform: [{ scale }], transformOrigin: "left center", marginVertical, alignSelf: 'flex-start' }}>
                      <TouchableOpacity
                        onPress={() => this.setState({ expandSuggestions: false, expandRolloverSuggestions: false })}
                        style={{ paddingVertical: 4, paddingHorizontal: 2 }}
                      >
                        <Text style={{ color: this.context.colors.trim, fontSize: this.context.sizes.default }}>close</Text>
                      </TouchableOpacity>
                    </View>
                  );
                })()}
              </ScrollView>
            );
          })()}

        </View>
      );
    }
    return null;
  };

  renderCalendarSuggestions = () => {
    let { calendarSuggestions } = this.state;
    if (!calendarSuggestions?.length) return null;
    return (
      <View>
        {calendarSuggestions.map((event) => {
          let timeStr = event.startAt
            ? textHelpers.formatTime(event.startAt)
            : null;
          return (
            <View
              key={event.id}
              style={{
                flexDirection: "row",
                alignItems: "center",
                paddingVertical: this.context.sizes.tiny,
              }}
            >
              <Ionicons
                name="calendar-outline"
                size={this.context.sizes.base}
                color={this.context.colors.trim}
                style={{ marginRight: this.context.sizes.small }}
              />
              <Text style={{ flex: 1, opacity: 0.6 }}>
                {timeStr ? `${timeStr} — ` : ""}
                {event.summary}
              </Text>
              <TouchableOpacity
                onPress={() => this.handleCalendarCreate(event)}
              >
                <AntDesign
                  name="pushpin"
                  style={[
                    styleSheet.bullet,
                    { backgroundColor: this.context.colors.empty, borderColor: this.context.colors.trim },
                  ]}
                  color={this.context.colors.trim}
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => this.handleCalendarDismiss(event)}
              >
                <Feather
                  name="x"
                  style={[
                    styleSheet.bullet,
                    { backgroundColor: this.context.colors.empty, borderColor: this.context.colors.trim },
                  ]}
                  color={this.context.colors.trim}
                />
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    );
  };

  renderInput = () => {
    let {
      chapter,
      rootChapter,
      onRefreshChapter,
      onUpdateChapter,
      autoCreateChapter,
      atemporal,
      moments,
      isToday,
    } = this.props;
    if (this.props.value?.category == "note") return null;
    return (
      <MomentComposer
        key={this.state.composerKey}
        text={this.state.composerText}
        inputRef={(c) => (this[`ref-composer`] = c)}
        defaultFocus={isToday}
        chapter={chapter}
        rootChapter={rootChapter}
        onRefreshChapter={onRefreshChapter}
        onUpdateChapter={onUpdateChapter}
        parentChapter={this.props.parentChapter}
        autoCreateChapter={autoCreateChapter}
        focusPrevious={() => this.handleFocusPrevious({ moment: "composer" })}
        focusNext={() => this.handleFocusNext({ moment: "composer" })}
        parentIsValue={this.props.parentIsValue}
        parentId={this.props.parentIsValue && this.props.parentId}
        parentIsChild={this.props.parentIsChild}
        preventAutoFocus={this.props.preventAutoFocus}
        onFocusPreviousChapter={this.props.onFocusPreviousChapter}
        onFocusNextChapter={this.props.onFocusNextChapter}
        atemporal={atemporal}
        onCollapseChapter={this.props.onCollapse}
        onExpandChapter={this.props.onExpand}
        getUnscopedChildren={
          !!this.props.getUnscopedChildren
            ? this.props.getUnscopedChildren
            : async () => {
                await this.setState({
                  showScratchPad: true,
                  separateTasks: false,
                });
                this.setMoments();
              }
        }
        // onMetaDown={!!tasks.length && }
        onMetaDown={this.props.onMetaDown}
        onFocusChapterTitle={this.props.onFocusChapterTitle}
        onFocus={() => {
          !!this.props.scrollToChapter && this.props.scrollToChapter();
          !!this.props.onFocusMoment && this.props.onFocusMoment(null);
        }}
        placeholder={this.props.composerPlaceholder}
        onIndent={
          !moments?.length
            ? undefined
            : () => {
                this.handleFocusNephew();
              }
        }
        disableSuggestions={this.props.disableSuggestions}
        onChangeText={this.props.onChangeText}
        onSubmit={this.props.onSubmit}
        defaultToNote={this.props.defaultToNote}
        createWithParams={this.props.createWithParams}
        onAddChild={this.props.onAddChild}
      />
    );
  };

  renderReferencers = () => {
    let { chapter, parentIsValue, parentId } = this.props;
    let value = store.getState().values.find((v) => v.id == parentId);
    if (value) {
      return (
        <BadgeRow
          tags
          index={0}
          // doublePressActions={["Pin", "Dismiss", "Snooze"]}
          moments={value.referencers?.map((r) => ({ value: r }))}
          // wrapperStyle={{ zIndex: 2 }}
          // titleTooltip={"Rollover Suggestions"}
          title={"Referenced by:"}
          // title={
          //   <MaterialCommunityIcons
          //     name={`application-import`}
          //     style={{
          //       fontSize: this.context.sizes.smallItemHeight,
          //       paddingRight: this.context.sizes.base,
          //     }}
          //     color={this.context.colors.trim}
          //   />
          // }
          chapter={chapter}
          rootChapter={chapter}
          onUpdateChapter={this.props.onUpdateChapter}
          // onPressTitle={async () => {
          //   await this.setState({
          //     expandRolloverSuggestions: !expandRolloverSuggestions,
          //   });
          // }}
          // includeAttribution={"left"}
        />
      );

      return (
        <ScrollView horizontal>
          {value.referencers.map((r, i) => {
            return <Tag key={i} value={r} />;
          })}
        </ScrollView>
      );
    }
  };

  handleSortItemUp = async (moment) => {
    let { parentIsValue, disableSorting } = this.props;
    if (disableSorting) return;
    if (parentIsValue) {
      // value children/parents list: rows are the units, no nesting to skip
      let to = this.moveMomentRow(moment, (from) => (from == 0 ? null : from - 1));
      this.focusMovedRow(moment, to);
    } else {
      let { chapterMoments } = this.props;
      let to = this.moveMomentRow(moment, (from, rows) => {
        if (from == 0) return null;
        let target = from - 1;
        // step over rows rendered nested under another pin — the unit above
        // moves as one, so land above the whole unit
        while (
          target > 0 &&
          !!chapterHelpers.isNested(chapterMoments, rows[target])
        ) {
          target--;
        }
        return target;
      });
      this.focusMovedRow(moment, to);
    }
  };

  handleSortItemDown = async (moment) => {
    let { chapterMoments, parentIsValue, disableSorting } = this.props;
    if (disableSorting) {
      return;
    }
    if (parentIsValue) {
      // value children/parents list: rows are the units, no nesting to skip
      let to = this.moveMomentRow(moment, (from, rows) =>
        from >= rows.length - 1 ? null : from + 1,
      );
      this.focusMovedRow(moment, to);
    } else {
      let to = this.moveMomentRow(moment, (from, rows) => {
        if (from >= rows.length - 1) return null;
        let target = from + 1;
        // step past rows rendered nested under another pin — land below the
        // whole unit rather than inside it
        while (
          target < rows.length - 1 &&
          !!chapterHelpers.isNested(chapterMoments, rows[target])
        ) {
          target++;
        }
        return target;
      });
      this.focusMovedRow(moment, to);
    }
  };

  handleSortItemToTop = async (moment) => {
    if (this.props.disableSorting) return;
    let to = this.moveMomentRow(moment, () => 0);
    this.focusMovedRow(moment, to);
  };

  handleSortItemToBottom = async (moment) => {
    if (this.props.disableSorting) return;
    let to = this.moveMomentRow(moment, (from, rows) => rows.length - 1);
    this.focusMovedRow(moment, to);
  };

  // Rebuilds the full chapter moment order from the current visual rows.
  // - the "spine" is every non-nested moment in the chapter's server order
  // - visible rows are re-slotted in the spine to match the new visual order;
  //   moments not rendered as rows (task row, filtered items) keep their slots
  // - nested children stay glued to their parent (inserted just before it,
  //   the existing server convention), so a parent+children unit moves as one
  buildSortedMomentIds = (rows) => {
    let { moments, chapterMoments } = this.props;
    let bundles = {};
    let nestedIds = new Set();
    (moments || []).forEach((m) => {
      let nestedUnder = chapterHelpers.isNested(chapterMoments, m);
      if (!!nestedUnder && nestedUnder.id != m.id) {
        if (!bundles[nestedUnder.id]) bundles[nestedUnder.id] = [];
        bundles[nestedUnder.id].push(m.id);
        nestedIds.add(m.id);
      }
    });

    let spine = (moments || [])
      .filter((m) => !nestedIds.has(m.id))
      .map((m) => m.id);
    let spineSet = new Set(spine);
    let rowIdSet = new Set((rows || []).map((r) => r.id));
    let orderedVisible = (rows || [])
      .map((r) => r.id)
      .filter((id) => spineSet.has(id));

    let vi = 0;
    let newSpine = spine.map((id) =>
      rowIdSet.has(id) ? orderedVisible[vi++] : id,
    );

    let full = [];
    newSpine.forEach((id) => {
      if (!!bundles[id]) full.push(...bundles[id]);
      full.push(id);
    });
    return full;
  };

  // Re-slots a value's child_ids/parent_ids to match the new visual row
  // order: ids rendered as rows take the new order, ids not rendered
  // (filtered, archived) keep their slots.
  buildSortedValueIds = (rows, currentIds) => {
    let idSet = new Set(currentIds);
    let orderedVisible = (rows || [])
      .map((r) => r.value?.id)
      .filter((id) => !!id && idSet.has(id));
    let visibleSet = new Set(orderedVisible);
    let vi = 0;
    return currentIds.map((id) => (visibleSet.has(id) ? orderedVisible[vi++] : id));
  };

  // Single entry point for row sorting (chapter pins and value
  // children/parents lists): instantly moves the visual row, then schedules
  // a debounced server sync so back-to-back moves don't wait on (or race) a
  // round-trip per keypress.
  moveMomentRow = (moment, computeTarget) => {
    let { chapter, parentIsValue, parentId, disableSorting } = this.props;
    if (disableSorting) return null;
    if (parentIsValue) {
      // rows here may be unpinned children with synthetic string ids —
      // sorting persists via the parent value's id arrays, not moment ids
      if (!parentId || !moment?.value?.id) return null;
    } else {
      if (!chapter?.id) {
        alert("no chapter found");
        return null;
      }
      if (!moment?.id || typeof moment.id != "number") {
        alert("no moment found");
        return null;
      }
    }
    let rows = [...this.state.moments];
    let from = rows.findIndex((m) => m.id == moment.id);
    if (from < 0) return null;
    let to = computeTarget(from, rows);
    if (to == null) return null;
    to = Math.max(0, Math.min(rows.length - 1, to));
    if (to == from) return null;
    let [row] = rows.splice(from, 1);
    rows.splice(to, 0, row);
    this.setState({ moments: rows });
    this.queueSortSync();
    return to;
  };

  queueSortSync = () => {
    if (this._sortSyncTimeout) clearTimeout(this._sortSyncTimeout);
    this._sortSyncTimeout = setTimeout(this.runSortSync, 400);
  };

  runSortSync = async () => {
    this._sortSyncTimeout = null;
    if (this._sortSyncInFlight) {
      // a request is already out — run again with the latest order on return
      this._sortSyncQueued = true;
      return;
    }
    this._sortSyncInFlight = true;
    let { parentIsValue, parentIsChild, parentId } = this.props;
    let ok = false;
    try {
      if (parentIsValue) {
        // persist through the value's ordered id array (parents list when
        // parentIsChild, children list otherwise)
        let value = store.getState().values.find((v) => v.id == parentId);
        if (!!value) {
          let key = parentIsChild ? "parent_ids" : "child_ids";
          let ids = this.buildSortedValueIds(this.state.moments, [
            ...(value[key] || []),
          ]);
          let res = await valueActions.update(parentId, { [key]: ids });
          // update() returns the value on success, false/conflict otherwise
          ok = !!res && !res.conflict;
        }
      } else {
        let momentIds = this.buildSortedMomentIds(this.state.moments);
        let res = await chapterActions.sortMoments(
          this.props.chapter?.id,
          momentIds,
        );
        ok = !!res?.success;
      }
    } catch (e) {
      console.warn("sort sync failed", e);
    }
    this._sortSyncInFlight = false;
    if (this._sortSyncQueued) {
      this._sortSyncQueued = false;
      this.runSortSync();
      return;
    }
    if (!ok && !this._unmounted) {
      // reorder didn't persist — fall back to the server's truth
      this._setMomentsCache = null;
      this.setMoments();
    }
  };

  // Re-focus the moved bullet at its new visual index (refs are keyed by the
  // index of the row in state.moments, and by value id for unpinned children)
  focusMovedRow = async (moment, to) => {
    if (!ENV.WEB || to == null) return;
    await new Promise((r) => setTimeout(r, 0));
    let ref = this[`ref-${to}-${moment.id || moment.value?.id}`];
    if (!!ref) ref.focus();
  };

  tryFocusScratchPad = () => {
    if (this.scratPadRef?.focus) {
      this.scratPadRef.focus();
      return true;
    }
    return false;
  };

  tryFocusMomentLog = () => {
    // Check if momentlog is visible using the separate momentsLogRef
    if (this.momentsLogRef?.focus) {
      this.momentsLogRef.focus();
      return true;
    }
    return false;
  };

  handleFocusFirstItem = () => {
    const { suggestions, rolloverSuggestions } = this.props;
    const { expandSuggestions, expandRolloverSuggestions } = this.state;
    if (expandSuggestions && suggestions?.length) {
      const first = suggestions[0];
      const ref = this[`ref-s-0-${first.id || first.value?.id}`];
      if (ref) { ref.focus(); return; }
    }
    if (expandRolloverSuggestions && rolloverSuggestions?.length) {
      const sorted = sortRolloverSuggestions(rolloverSuggestions);
      const first = sorted[0];
      const ref = this[`ref-r-0-${first.id || first.value?.id}`];
      if (ref) { ref.focus(); return; }
    }
    this.handleFocusFirstMoment();
  };

  handleFocusFirstMoment = async (id = undefined) => {
    this.handleFocusNext({ index: -1, id });
  };

  // "next section" jump from the suggestions: first pinned item, or the
  // composer when the list is empty — the scratchpad sentinel id skips
  // the scratchpad-first branch in handleFocusNext
  focusFirstMomentOrComposer = () => {
    this.handleFocusNext({ index: -1, id: "scratchpad" });
  };

  handleFocusComposer = async () => {
    let ref = this[`ref-composer`];
    if (!!ref) {
      await this.setState({ hideCaret: true });
      ref.focus();
      if (!!ref.setSelectionRange) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        ref.setSelectionRange(0, 0);
        await this.setState({ hideCaret: false });
      }
    }
  };

  handleFocusNext = async ({
    moment = undefined,
    id = undefined,
    index = null,
    comingFromChild = false,
  }) => {
    let { moments } = this.state;
    let ref = null;
    if (moment == "composer") {
      // If a tarot bullet sits beneath the list, down-arrow focuses it first.
      if (this.props.focusTarotBelow) {
        this.props.focusTarotBelow();
        return;
      }
      if (this.props.focusNextUncle) this.props.focusNextUncle();
      else if (this.props.onFocusSubChapter) this.props.onFocusSubChapter();
      else if (this.props.parentIsChild && this.props.focusParentMoment) {
        this.props.focusParentMoment();
      }
      return;
    } else if (index != null) {
      let thisRef = this[`bullet-ref-${index}-${id}`];
      if (thisRef?.state?.expanded && !comingFromChild) {
        if (!!thisRef.handleFocusFirstChild) {
          thisRef.handleFocusFirstChild();
          return;
        }
      } else if (
        !thisRef?.state?.expanded &&
        !comingFromChild &&
        thisRef?.state?.hasPinnedChildren &&
        !thisRef?.state?.hidePinnedChildren
      ) {
        if (!!thisRef.handleFocusFirstChild) {
          thisRef.handleFocusFirstChild();
          return;
        }
      }
      let nextIndex = index + 1;
      if (
        nextIndex == 0 &&
        this.state.showScratchPad &&
        !!this.scratPadRef &&
        id != "scratchpad"
      ) {
        try {
          this.scratPadRef.focus();
        } catch (e) {
          console.error("catch", e);
        }
      } else if (!moments?.length || nextIndex >= moments?.length) {
        console.log("foudn you");
        if (this.props.pinnedMomentList && !!this.props.focusNextUncle) {
          console.log("focus uncle");
          this.props.focusNextUncle();
          return;
        }
        ref = this[`ref-composer`];
        console.log("no more moments, so focus composer", ref);
      } else {
        console.log("final else");
        let nextMoment = moments[nextIndex];
        let nextId = nextMoment.id || nextMoment.value?.id;
        ref = this[`ref-${nextIndex}-${nextId}`];
        // console.log("try to focus next moment,", nextMoment, ref);
        if (!ref) {
          // console.log("focus compoer instead");
          ref = this[`ref-composer`];
        }
      }
    }
    // } else {if (moment?.id) {
    //   let momentIndex = moments.findIndex((m) => m.id == moment.id);
    //   let nextIndex = momentIndex + 1;
    //   if (nextIndex >= moments.length) {
    //     ref = this[`ref-composer`];
    //   } else {
    //     let nextMoment = moments[nextIndex];
    //     console.log(moment, momentIndex, nextMoment, moments);
    //     ref = this[`ref-${nextMoment.id}`];
    //   }
    // }
    if (!!ref) {
      await this.setState({ hideCaret: true });
      ref.focus();
      console.log("focusing ref", ref);
      if (!!ref.setSelectionRange) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        ref.setSelectionRange(0, 0);
        await this.setState({ hideCaret: false });
      }
    }
  };

  handleFocusPrevious = async ({ moment, id = undefined, index = null }) => {
    let { moments } = this.state;
    let ref = null;
    if (moment == "self") {
      ref = this[`ref-${index}-${id}`];
      console.log("focus self!!!", id, ref, index, `ref-${index}-${id}`);
    } else if (moment == "composer") {
      console.log("focus previous from composer", moments?.length);
      if (!!moments?.length) {
        let indexFromEnd = 0;
        while (!ref && indexFromEnd <= moments.length - 1) {
          let index = moments.length - 1 - indexFromEnd;
          let lastMoment = moments[index];
          ref = this[`ref-${index}-${lastMoment.id || lastMoment.value?.id}`];
          indexFromEnd++;
        }
      } else {
        if (!!this.props.focusParentMoment) {
          this.props.focusParentMoment();
        } else if (this.props.onFocusChapterTitle) {
          this.props.onFocusChapterTitle();
        }
        return;
      }
    } else if (index != null) {
      if (index == 0) {
        let { expandSuggestions, expandRolloverSuggestions } = this.state;
        let { suggestions, rolloverSuggestions } = this.props;
        if (expandRolloverSuggestions && rolloverSuggestions?.length) {
          const sr = [...rolloverSuggestions].sort((a, b) => (a.value?.days_old ?? 0) - (b.value?.days_old ?? 0));
          let lastIdx = sr.length - 1;
          let last = sr[lastIdx];
          let ref = this[`ref-r-${lastIdx}-${last.id || last.value?.id}`];
          if (ref) {
            await this.setState({ hideCaret: true });
            ref.focus();
            if (ref.setSelectionRange) {
              await new Promise((resolve) => setTimeout(resolve, 0));
              ref.setSelectionRange(0, 0);
            }
            await this.setState({ hideCaret: false });
            return;
          }
        }
        if (expandSuggestions && suggestions?.length) {
          let lastIdx = suggestions.length - 1;
          let last = suggestions[lastIdx];
          let ref = this[`ref-s-${lastIdx}-${last.id || last.value?.id}`];
          if (ref) {
            await this.setState({ hideCaret: true });
            ref.focus();
            if (ref.setSelectionRange) {
              await new Promise((resolve) => setTimeout(resolve, 0));
              ref.setSelectionRange(0, 0);
            }
            await this.setState({ hideCaret: false });
            return;
          }
        }
        if (this.props.focusParentMoment) this.props.focusParentMoment();
        else if (this.props.onFocusChapterTitle)
          this.props.onFocusChapterTitle();
        return;
      }
      let prevIndex = index - 1;
      let prevMoment = moments[prevIndex];
      let prevId = prevMoment.id || prevMoment.value?.id;
      console.log(prevIndex, prevMoment, moments);
      ref = this[`ref-${prevIndex}-${prevId}`];
    }
    // } else if (moment?.id) {
    //   let momentIndex = moments.findIndex((m) => m.id == moment.id);
    //   if (momentIndex == 0) {
    //     this.props.focusParentMoment && this.props.focusParentMoment();
    //     return;
    //   }
    //   let prevIndex = momentIndex - 1;
    //   let prevMoment = moments[prevIndex];
    //   ref = this[`ref-${prevMoment.id}`];
    // }
    if (!!ref) {
      await this.setState({ hideCaret: true });
      ref.focus();
      await new Promise((resolve) => setTimeout(resolve, 0));
      ref.setSelectionRange(0, 0);
      await this.setState({ hideCaret: false });
    } else {
      console.log(moment, id, index);
      console.log("no ref found", moments, this);
    }
  };

  handleFocusNephew = async () => {
    let { moments } = this.state;
    let ref = null;
    let refId = "";
    if (!!moments?.length) {
      let indexFromEnd = 0;
      while (!ref && indexFromEnd <= moments.length - 1) {
        let index = moments.length - 1 - indexFromEnd;
        let lastMoment = moments[index];
        refId = `bullet-ref-${index}-${lastMoment.id}`;
        ref = this[refId];
        // ref = this[`ref-${index}-${lastMoment.id}`];
        indexFromEnd++;
      }
    }
    if (!!ref) {
      ref.toggleExpanded(true);
    } else {
      console.log("no ref found", moments, refId, this);
    }
  };

  handleSuggestionActions = async () => {
    alert("dismiss all?");
    // let subtitle = ENV.WEB
    //   ? null
    //   : value.title || value.text;
    // let res = await alertActions.confirm({
    //   options: doublePressActions,
    //   title: "Take Action",
    //   subtitle,
    // });
    // this.handleActionRes(value, res, doublePressActions);
  };

  styles = () => ({});
}
